import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  DateFormat,
  formatDate,
  formatName,
  formatTime,
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  pluralize,
  ProfileImageStack,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/compat/router';
import Image from 'next/image';
import Link from 'next/link';
import { type FC, useState } from 'react';

import EventPlaceholderImage from '../../assets/images/events/event-placeholder.png';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog/posthog';
import { getClassUrl } from '../../utils/classes';
import { DEFAULT_ERROR_MESSAGE } from '../../utils/constants';
import { getEventUrl } from '../../utils/events';
import { useOnce } from '../../utils/hooks/useOnce';
import { wasMutationSuccessful } from '../../utils/mutations';
import { AddToCalendarButton } from '../coaching-session/AddToCalendarButton';
import CheckboxInput from '../inputs/CheckboxInput';
import { type CategoryTypeaheadCategoryFragment } from '../typeahead/__generated-gql-types__/CategoryTypeahead.generated';

import { EventItemSkeleton } from './EventItemSkeleton';
import {
  type MultiEventRegistrationModalClassFragment,
  type MultiEventRegistrationModalCoachFragment,
  type MultiEventRegistrationModalEventFragment,
  useMultiEventRegistrationModalBatchEnrollMutation,
  useMultiEventRegistrationModalEventsQuery,
} from './__generated-gql-types__/MultiEventRegistrationModal.generated';
interface BaseEvent {
  id: string;
  /**
   * whether this event is on platform or Luma event.
   * Only on-platform events could add to calendar via the button
   */
  isOnPlatformEvent: boolean;
  parentId?: string;
  startAt: number;
  name: string;
  category: CategoryTypeaheadCategoryFragment;
  topFacePiles: Array<{
    name: string;
    pictureLink: string;
  }>;
  imageLink: string;
  eventLink: string;
  coaches: MultiEventRegistrationModalCoachFragment[];
}

const convertToBaseEvent = (
  event: MultiEventRegistrationModalEventFragment,
): BaseEvent | null => {
  if (event.__typename === 'Shindig') {
    if (!event.categories?.[0]?.category) {
      return null;
    }
    return {
      id: event.shindigId,
      isOnPlatformEvent: false,
      startAt: event.date,
      name: event.shindigName,
      category: event.categories[0].category,
      imageLink: event.imageLink ?? '',
      eventLink: event.link,
      coaches: event.coaches ?? [],
      topFacePiles:
        event.guests?.slice(0, 3).map((guest) => ({
          name: guest.name ?? '',
          pictureLink: '',
        })) ?? [],
    };
  } else if (event.__typename === 'Class') {
    const session = event.upcomingSessionsV2?.[0];
    if (!session || !event.coach || !event.category) {
      return null;
    }
    return {
      id: session.id,
      isOnPlatformEvent: true,
      parentId: event.id,
      startAt: session.startAt,
      name: event.name ?? '',
      category: event.category,
      imageLink: event.imageLink ?? '',
      eventLink: getClassUrl(event.slug ?? '', event.isFreeEvent ?? false),
      coaches: event.coach ? [event.coach] : [],
      topFacePiles: session.topFacePiles.map((link) => ({
        name: '',
        pictureLink: link,
      })),
    };
  }

  return null;
};

interface MultiEventRegistrationModalHookProps {
  event: MultiEventRegistrationModalClassFragment;
  trackingPrefix: string;
  email?: string;
}

interface MultiEventRegistrationModalProps
  extends MultiEventRegistrationModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  trackingPrefix: string;
  email?: string;
}

const MultiEventRegistrationModalContent: FC<
  MultiEventRegistrationModalProps
> = ({ open, setOpen, trackingPrefix, event, email }) => {
  const [hasRegistered, setHasRegistered] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<BaseEvent[]>([]);

  return (
    <Modal open={open} onOpenChange={setOpen}>
      {!hasRegistered ? (
        <MultiEventsRegistrationContent
          event={event}
          selectedEvents={selectedEvents}
          setSelectedEvents={setSelectedEvents}
          setHasRegistered={setHasRegistered}
          trackingPrefix={trackingPrefix}
          email={email}
        />
      ) : (
        <MultiEventsRegistrationSuccessContent
          event={event}
          events={selectedEvents}
          email={email}
        />
      )}
    </Modal>
  );
};

interface BrowseMoreEventsButtonProps {
  category?: Nullable<CategoryTypeaheadCategoryFragment>;
}
const BrowseMoreEventsButton: FC<BrowseMoreEventsButtonProps> = ({
  category,
}) => {
  const router = useRouter();
  return (
    <Button
      type="submit"
      label="Browse more events"
      buttonColor={ButtonColor.PRIMARY}
      width={ButtonWidth.FULL}
      size={ButtonSize.MEDIUM}
      onClick={async () => {
        await router?.push(getEventUrl(category));
      }}
      id="multi_event_registration_modal--browse_more"
    />
  );
};

interface MultiEventsRegistrationContentProps {
  event: MultiEventRegistrationModalClassFragment;
  selectedEvents: BaseEvent[];
  setSelectedEvents: (events: BaseEvent[]) => void;
  setHasRegistered: (hasRegistered: boolean) => void;
  trackingPrefix: string;
  email?: string;
}
const MultiEventsRegistrationContent: FC<
  MultiEventsRegistrationContentProps
> = ({
  event,
  selectedEvents,
  setSelectedEvents,
  setHasRegistered,
  trackingPrefix,
  email,
}) => {
  const { data: eventsData, loading } =
    useMultiEventRegistrationModalEventsQuery({
      variables: {
        categorySlugOrUrnFilter: event.category?.id ? event.category.id : [],
      },
      fetchPolicy: 'cache-and-network',
    });
  const [batchEnroll, { loading: batchEnrollLoading }] =
    useMultiEventRegistrationModalBatchEnrollMutation();

  const [error, setError] = useState<string | null>(null);

  const rawEvents = eventsData?.searchClassesAndShindigs.results ?? [];
  const events = rawEvents
    .map(convertToBaseEvent)
    .filter((event): event is BaseEvent => event !== null);

  useOnce(() => {
    setSelectedEvents(events);
  }, !loading);

  const session = event.upcomingSessionsV2?.[0];
  if (!session) {
    return null;
  }

  const onRegister = async () => {
    if (!email) {
      setError(DEFAULT_ERROR_MESSAGE);
      return;
    }
    const res = await batchEnroll({
      variables: {
        email,
        input: selectedEvents.map((event) => event.id),
      },
    });
    if (wasMutationSuccessful(res)) {
      selectedEvents
        .filter((event) => event.isOnPlatformEvent && event.parentId)
        .map((event) => {
          mxpnl.track('Enroll Free Event', {
            eventId: event.parentId,
            eventName: event.name,
            sessionId: event.id,
            location: `${trackingPrefix} - MultiEventRegistrationModal`,
          });
          phog.capture('free_event--enroll', {
            eventId: event.parentId,
            eventName: event.name,
            coachingSessionId: event.id,
            location: `${trackingPrefix} - MultiEventRegistrationModal`,
          });
        });
      setHasRegistered(true);
    } else {
      setError(DEFAULT_ERROR_MESSAGE);
    }
  };

  return (
    <ModalContent
      footer={
        <div className="p-2">
          {error ? <div className="mb-1 text-red-500">{error}</div> : null}
          {events.length > 0 || loading ? (
            <Button
              type="submit"
              label={`Register for ${selectedEvents.length > 0 ? pluralize(selectedEvents.length, 'event') : 'events'}`}
              buttonColor={ButtonColor.PRIMARY}
              width={ButtonWidth.FULL}
              size={ButtonSize.MEDIUM}
              disabled={selectedEvents.length === 0 || batchEnrollLoading}
              onClick={onRegister}
              id="multi_event_registration_modal--register"
            />
          ) : (
            <BrowseMoreEventsButton category={event.category} />
          )}
        </div>
      }
      size={ModalSize.SMALL}
    >
      <div className="divide-y">
        <div className="bg-[url('/images/Hero-Centered-Tall.png')] bg-cover bg-no-repeat px-4 py-6 text-center">
          <h2 className="text-4xl font-medium">You&apos;re in!</h2>
          <div className="mt-4 font-medium">{event.name}</div>
          <div className="text-leland-gray-light">
            {formatDate(session.startAt, DateFormat.LONG_MONTH_DAY)} @{' '}
            {formatTime(session.startAt)} ·{' '}
            <AddToCalendarButton
              scheduledSessionIds={[session.id]}
              email={email}
              justText
            />
          </div>
        </div>
        {loading || events.length > 0 ? (
          <div className="p-4">
            <div className="font-medium text-leland-gray-light">
              Quick register for related events:
            </div>
            {loading ? (
              <EventItemSkeleton />
            ) : (
              <div className="flex flex-col divide-y divide-leland-gray-stroke">
                {events.map((event) => (
                  <EventLineItem
                    key={event.id}
                    event={event}
                    checked={
                      selectedEvents.find((e) => e.id === event.id) !==
                      undefined
                    }
                    onToggle={(checked) => {
                      if (checked) {
                        setSelectedEvents([...selectedEvents, event]);
                      } else {
                        setSelectedEvents(
                          selectedEvents.filter((e) => e.id !== event.id),
                        );
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </ModalContent>
  );
};

interface MultiEventsRegistrationSuccessContentProps {
  event: MultiEventRegistrationModalClassFragment;
  events: BaseEvent[];
  email?: string;
}
const MultiEventsRegistrationSuccessContent: FC<
  MultiEventsRegistrationSuccessContentProps
> = ({ event, events, email }) => {
  const addToCalendarIds = events
    .filter((event) => event.isOnPlatformEvent) // only on-platform events could add to calendar
    .map((event) => event.id);
  return (
    <ModalContent
      footer={
        <div className="p-2">
          <BrowseMoreEventsButton category={event.category} />
        </div>
      }
      size={ModalSize.SMALL}
    >
      <div className="divide-y">
        <div className="flex flex-col items-center bg-[url('/images/Hero-Centered-Tall.png')] bg-cover bg-no-repeat p-4 text-center">
          <h2 className="text-4xl font-medium">You&apos;re in!</h2>
          <div className="mb-2 mt-4 text-leland-gray-light">
            You have successfully registered for {events.length} more{' '}
            {pluralize(events.length, 'event', { excludeNumber: true })}
          </div>
          {addToCalendarIds.length > 0 ? (
            <AddToCalendarButton
              scheduledSessionIds={addToCalendarIds}
              label="Add all events to calendar"
              email={email}
            />
          ) : null}
        </div>
        <div className="p-4">
          <div className="flex flex-col divide-y divide-leland-gray-hover">
            {events.map((event) => (
              <div key={event.id} className="space-y-2 p-4">
                <Link
                  href={event.eventLink}
                  className="text-lg font-medium underline"
                  id="multi_event_modal_success--event_link"
                >
                  {event.name}
                </Link>
                <div className="text-leland-gray-light">
                  {formatDate(event.startAt, DateFormat.LONG_MONTH_DAY)} @{' '}
                  {formatTime(event.startAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalContent>
  );
};

interface EventLineItemProps {
  event: BaseEvent;
  checked: boolean;
  onToggle: (checked: boolean) => void;
}
const EventLineItem: FC<EventLineItemProps> = ({
  event,
  checked,
  onToggle,
}) => {
  return (
    <div className="flex items-start justify-between py-8">
      <div className="flex gap-4">
        <Image
          className="aspect-entity-image rounded object-cover"
          src={event.imageLink ?? EventPlaceholderImage}
          alt="Event cover"
          width={100}
          height={52}
        />
        <div>
          <Link
            href={event.eventLink}
            className="text-lg font-medium underline"
            id="multi_event_modal--event_link"
          >
            {event.name}
          </Link>
          <div className="flex gap-2">
            <ProfileImageStack
              profiles={event.topFacePiles.map(({ name, pictureLink }) => ({
                pictureLink,
                firstName: name.split(' ')[0],
                lastName: name.split(' ')[1],
              }))}
            />
            {event.coaches.length > 0 ? (
              <div>
                Featuring{' '}
                {formatName({
                  firstName: event.coaches[0].user.firstName,
                  lastName: event.coaches[0].user.lastName,
                })}
                {event.coaches.length > 1
                  ? event.coaches.length === 2
                    ? ` + ${formatName({
                        firstName: event.coaches[1].user.firstName,
                        lastName: event.coaches[1].user.lastName,
                      })}`
                    : ` + ${event.coaches.length - 1} more`
                  : null}
              </div>
            ) : null}
          </div>
          <div className="text-leland-gray-light">
            {formatDate(event.startAt, DateFormat.LONG_MONTH_DAY)} @{' '}
            {formatTime(event.startAt)}
          </div>
        </div>
      </div>
      <CheckboxInput label="" isChecked={checked} onToggle={onToggle} />
    </div>
  );
};

export const useMultiEventRegistrationModal: ModalHook<
  MultiEventRegistrationModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();

  const MultiEventRegistrationModal: FC<
    MultiEventRegistrationModalHookProps
  > = (props) => {
    return open ? (
      <MultiEventRegistrationModalContent
        {...props}
        open={open}
        setOpen={setOpen}
      />
    ) : null;
  };
  return { open, setOpen, Modal: MultiEventRegistrationModal };
};
